import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, Handshake, Trash2 } from 'lucide-react';
import { SortableList } from './SortableList';
import { ItemActions } from './ItemActions';
import { Dropzone } from './Dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => (!src ? '' : src.startsWith('http') ? src : `${BACKEND_URL}${src}`);

const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchPartners = async () => {
    const res = await adminApi.get('/partners');
    setPartners(res.data || []);
  };
  useEffect(() => {
    fetchPartners();
  }, []);

  const blank = () => ({
    name: '',
    logo: '',
    link_url: '',
    order: partners.length,
    published: true,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editing.name?.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      if (editing.id && partners.find((x) => x.id === editing.id)) {
        await adminApi.put(`/partners/${editing.id}`, editing);
      } else {
        await adminApi.post('/partners', { ...editing, id: undefined });
      }
      toast({ title: 'Saved', description: `${editing.name} saved successfully` });
      setEditing(null);
      fetchPartners();
    } catch (err) {
      toast({
        title: 'Error',
        description: err?.response?.data?.detail || 'Save failed',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Remove this supporter logo?')) return;
    await adminApi.delete(`/partners/${id}`);
    toast({ title: 'Removed' });
    fetchPartners();
  };

  const handleReorder = async (reordered) => {
    setPartners(reordered);
    try {
      await adminApi.put('/partners/reorder', { ids: reordered.map((r) => r.id) });
    } catch {
      // silent
    }
  };

  return (
    <div className="space-y-6" data-testid="partners-manager">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="flex items-center gap-2">
          <Handshake className="w-6 h-6 text-[#1565C0]" />
          <div>
            <h2 className="text-2xl font-bold">Supported by Leaders</h2>
            <p className="text-xs text-gray-500">
              Add, remove or reorder the partner / supporter logos on the homepage.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setEditing(blank())}
          className="bg-[#1565C0]"
          data-testid="add-partner-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Supporter
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>{editing.id ? 'Edit Supporter' : 'New Supporter'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label htmlFor="partner-name">Supporter Name</Label>
                <Input
                  id="partner-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  required
                  data-testid="partner-name-input"
                  placeholder="e.g. Tourism Malaysia"
                />
              </div>

              <div>
                <Label>Logo</Label>
                {editing.logo && (
                  <div className="mt-2 relative inline-block bg-gray-50 border rounded p-3">
                    <img
                      src={resolveImg(editing.logo)}
                      alt="Logo preview"
                      className="max-h-24 max-w-[200px] object-contain"
                      data-testid="partner-logo-preview"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-7 w-7 p-0 rounded-full"
                      onClick={() => setEditing({ ...editing, logo: '' })}
                      data-testid="partner-remove-logo-btn"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                <div className="mt-2">
                  <Dropzone
                    compact
                    accept="image/*"
                    onUpload={(url) => setEditing({ ...editing, logo: url })}
                    label="Drop logo image (PNG with transparent bg recommended)"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Or paste a direct URL below.
                </p>
                <Input
                  className="mt-1"
                  value={editing.logo || ''}
                  onChange={(e) => setEditing({ ...editing, logo: e.target.value })}
                  placeholder="https://... or /api/uploads/partner_x.png"
                  data-testid="partner-logo-input"
                />
              </div>

              <div>
                <Label htmlFor="partner-link">Click-through URL (optional)</Label>
                <Input
                  id="partner-link"
                  value={editing.link_url || ''}
                  onChange={(e) => setEditing({ ...editing, link_url: e.target.value })}
                  placeholder="https://tourism.gov.my"
                  data-testid="partner-link-input"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="partner-published"
                  checked={editing.published !== false}
                  onCheckedChange={(v) => setEditing({ ...editing, published: v })}
                  data-testid="partner-published-switch"
                />
                <Label htmlFor="partner-published">Published (visible on homepage)</Label>
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-[#1565C0]"
                  data-testid="save-partner-btn"
                >
                  {loading ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={partners}
        onReorder={handleReorder}
        renderItem={(p) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move flex-shrink-0" />
              <div className="w-20 h-14 flex-shrink-0 bg-gray-50 border rounded flex items-center justify-center overflow-hidden">
                {p.logo ? (
                  <img
                    src={resolveImg(p.logo)}
                    alt={p.name}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <span className="text-[10px] text-gray-400">No logo</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm truncate" data-testid={`partner-name-${p.id}`}>
                  {p.name}
                </h3>
                {p.link_url && (
                  <a
                    href={p.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#1565C0] hover:underline truncate block"
                  >
                    {p.link_url}
                  </a>
                )}
                {!p.published && (
                  <span className="inline-block mt-0.5 text-[10px] uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    Hidden
                  </span>
                )}
              </div>
              <ItemActions
                collection="partners"
                item={p}
                onChanged={fetchPartners}
                onEdit={setEditing}
                onDelete={handleDelete}
              />
            </CardContent>
          </Card>
        )}
      />
      {partners.length === 0 && !editing && (
        <p className="text-center text-gray-500 py-8">
          No supporters yet. Click "Add Supporter" to create one.
        </p>
      )}
    </div>
  );
};

export default PartnersManager;
